<template>
  <v-flex class="location select" :class="{ 'app-toolbar px-2': app }">
    <!-- z-index 300 to cater for safari bug -->
    <v-autocomplete
      @change="handleChange"
      @focus="handleFocus"
      :label="label"
      v-model="selected"
      :style="toFront ? 'z-index: 300' : ''"
      color="primary"
      :search-input.sync="searchTerm"
      :placeholder="searchTerm"
      :disabled="disabled"
      :required="required"
      :rules="rules || []"
      :loading="loading || fetching"
      :items="options"
      item-value="id"
      :hint="hint"
      append-icon=""
      return-object
      hide-no-data
      no-filter
      clearable
      :solo="solo"
      :attach="attach"
      :dense="dense"
      :hide-details="hideDetails"
      ref="autocomplete"
      @blur="$emit('blur')"
      @keyup.enter="$emit('keyup.enter')"
    >
      <template slot="item" slot-scope="{ item: place }">
        <v-list-tile-avatar v-if="$vuetify.breakpoint.smAndUp && !dense">
          <v-icon>mdi-map-marker</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content v-if="place.structured_formatting">
          <v-list-tile-title>
            <span v-if="place.structured_formatting.main_text">
              {{ place.structured_formatting.main_text }}
            </span>
          </v-list-tile-title>
          <v-list-tile-sub-title>
            <span v-if="place.structured_formatting.secondary_text">
              {{ place.structured_formatting.secondary_text }}
            </span>
          </v-list-tile-sub-title>
        </v-list-tile-content>
        <v-list-tile-content v-else-if="place.description">
          <v-list-tile-title>
            <span>
              {{ place.description }}
            </span>
          </v-list-tile-title>
        </v-list-tile-content>
      </template>
      <template slot="selection" slot-scope="{ item: place }">
        <div :class="{ 'caption selection--dense': dense }">
          <v-avatar v-if="$vuetify.breakpoint.smAndUp" :small="dense">
            <v-icon>mdi-map-marker</v-icon>
          </v-avatar>
          <span
            v-if="place.description"
            class="selection-text"
            :style="`max-width: ${$vuetify.breakpoint.xsOnly ? '90%' : '80%'};`"
          >
            {{ place.description }}
          </span>
        </div>
      </template>
      <template slot="append-item">
        <div class="text-xs-center">
          <img
            src="/img/powered_by_google_on_white.png"
            alt="google attribution"
            height="12"
          />
        </div>
      </template>
    </v-autocomplete>
  </v-flex>
</template>

<script>
// Helpers
import _ from "lodash";
import { mapGetters } from "vuex";

export default {
  created() {
    this.loadGoogleMapsScript();
    if (this.value) this.selected = { id: "id", description: this.value };
  },

  props: {
    app: { type: Boolean, default: () => false },
    attach: { type: Boolean, default: true },
    autofocus: { type: Boolean, default: false },
    dense: { type: Boolean, default: false },
    disabled: Boolean,
    hideDetails: { type: Boolean, default: false },
    hint: { type: String },
    label: { type: String, default: () => "Enter a city" },
    loading: { type: Boolean, default: () => false },
    required: Boolean,
    rules: Array,
    solo: { type: Boolean, default: false },
    toFront: { type: Boolean, default: () => true },
    value: String,
    coordinates: { type: Boolean, default: false },
  },

  data() {
    return {
      googleService: null,
      googleSession: null,
      selected: {},
      fetching: false,
      searchTerm: "",
      places: [],
    };
  },

  computed: {
    isMapsLoaded() {
      return typeof google !== "undefined" && google.maps;
    },

    options() {
      const places = _.get(this, "places[0]") ? this.places : [];

      if (_.get(this, "selected.id")) return [this.selected, ...places];

      return places;
    },
  },

  watch: {
    searchTerm: _.debounce(function (search) {
      this.$emit("search", search);
      // search is null on initial load
      if (search && search.length > 2) {
        this.search();
      } else {
        this.places = [];
      }
    }, 300),
  },

  methods: {
    handleChange(selected) {
      if (selected && selected.place_id) {
        let service = new google.maps.places.PlacesService(
          document.createElement("div")
        );

        service.getDetails(
          {
            placeId: selected.place_id,
            fields: ["formatted_address", "geometry"],
          },
          (place, status) => {
            if (status == google.maps.places.PlacesServiceStatus.OK) {
              this.selected = {
                id: "id",
                ...this.selected,
                ...JSON.parse(JSON.stringify(place)),
              };
              this.$emit(
                "input",
                this.coordinates
                  ? `${this.selected.coordinates.lat},${this.selected.coordinates.lon}`
                  : this.selected
              );
            }
          }
        );
      }
    },

    handleFocus() {
      if (typeof google !== "undefined" && !this.googleService) {
        this.googleService = new google.maps.places.AutocompleteService();
      }
      if (typeof google !== "undefined" && !this.googleSession) {
        this.googleSession = new google.maps.places.AutocompleteSessionToken();
      }
      this.$emit("focus");
    },

    loadGoogleMapsScript(callback) {
      if (this.isMapsLoaded) return;

      // Load google maps API
      let script = document.createElement("script");
      script.setAttribute(
        "src",
        `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_GOOGLE_API_KEY}&libraries=places`
      );
      if (callback) script.onload = callback;
      document.head.appendChild(script);
    },

    search() {
      if (!_.get(this, "googleService")) return;

      this.fetching = true;
      this.googleService.getPlacePredictions(
        {
          input: this.searchTerm,
          sessionToken: this.googleSession,
        },
        (result) => {
          this.places = result;
          this.fetching = false;
        }
      );
    },
  },
};
</script>

<style lang="scss">
.location.select {
  &.app-toolbar {
    width: 30rem;
    max-width: 60vw;

    .v-input__slot {
      margin: auto;
      overflow: hidden;
      width: 100%;
    }
  }

  .selection-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .selection--dense {
    display: table;
    vertical-align: middle;
    max-width: 99%;
    // margin: 0.1rem 0;

    * {
      display: table-cell;
      vertical-align: middle;
      padding: 0px;
    }
  }

  .v-select__selections {
    max-width: 90%;
  }

  .v-text-field--solo:not(.v-text-field--solo-flat) .v-input__slot {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
    border-radius: 4px !important;
    border: 1px solid #ebebeb !important;
  }
}
</style>
